import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
  Textarea,
} from '@heroui/react'

import { gifDisplayTitle } from '../../gifs/types'
import type { Gif } from '../../gifs/types'
import {
  getRecommendations,
  getRetrievalPool,
} from '../../recommendations/services/recommendationService'
import { useToast } from '../../../hooks'
import {
  ExplicitRating,
  type FixtureNoteReaction,
  type FixtureNoteStanceReaction,
} from '../../../../functions/src/shared/types/gif'

import { GifThumbnail } from './GifThumbnail'
import { RecordPeek } from './RecordPeek'

/** Verdict a reaction card can carry — matches the triage inbox's vocabulary. */
type Verdict = 'good' | 'trap' | 'skip'

/** The one-tap reason chips offered under each reaction card. */
const REASON_CHIPS = [
  'wrong tone',
  'too generic',
  'wrong meaning',
  'too mean',
  'not this situation',
  'love this vibe',
] as const

const POOL_K = 15

/** Backend caps retrieveCandidatePool at k=60 — past that the pool is dry. */
const POOL_MAX_K = 60

interface QueuedCandidate {
  gifId: string
  /** The recommender's stance label, when this candidate came from the board. */
  stanceLabel: string | null
  /** The recommender's per-pick reasoning, when present. */
  reasoning: string | null
}

/** A verdict plus its optional chips/comment, keyed by gifId in state. */
interface ReactionState {
  verdict: Verdict
  reasons: string[]
  comment: string
}

interface StanceContext {
  label: string
  rationale: string
}

export interface FixtureReactionPassProps {
  conversationSnippet: string
  userIntent: string
  preset: string
  gifById: Map<string, Gif>
  /** True when the note holds an edited (non-draft) value — flips to revision. */
  hasNote: boolean
  /** True while the parent's draft call is in flight (drives the button spinner). */
  drafting: boolean
  /** Mirror a verdict into the parent's buckets (t → traps, y → good). */
  onAssignBucket: (gifId: string, bucket: 'traps' | 'good') => void
  /** Remove a gif from all buckets (undo). */
  onRemoveBucket: (gifId: string) => void
  /**
   * Hand the collected reactions up to the parent, which runs the single draft
   * call (folding in the trap/forbidden/good buckets it owns) and applies the
   * per-field draft results.
   */
  onDraft: (
    reactions: FixtureNoteReaction[],
    stanceReactions: FixtureNoteStanceReaction[]
  ) => void
}

/**
 * The "Explore & react" pass: surface concrete candidates (recommender board +
 * retrieval-pool sample) and let the author react fast (y/t/n/u + reason chips).
 * Reactions mirror into the parent's traps/known-good buckets live; on "Draft"
 * the collected reactions are handed to the parent, which runs the single
 * draftFixtureMeta call (merging its buckets) and drops the results into the
 * form fields as drafts for the author to edit + approve.
 */
export function FixtureReactionPass({
  conversationSnippet,
  userIntent,
  preset,
  gifById,
  hasNote,
  drafting,
  onAssignBucket,
  onRemoveBucket,
  onDraft,
}: FixtureReactionPassProps) {
  const toast = useToast()

  const [exploring, setExploring] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [poolExhausted, setPoolExhausted] = useState(false)
  const [queue, setQueue] = useState<QueuedCandidate[]>([])
  const [stances, setStances] = useState<StanceContext[]>([])
  const [cursor, setCursor] = useState(0)
  const [reactions, setReactions] = useState<Record<string, ReactionState>>({})
  const [stanceReactions, setStanceReactions] = useState<
    Record<string, FixtureNoteStanceReaction['verdict']>
  >({})

  const hasContext = !!conversationSnippet.trim() || !!userIntent.trim()

  const nonSkipCount = useMemo(
    () => Object.values(reactions).filter((r) => r.verdict !== 'skip').length,
    [reactions]
  )

  const finished = queue.length > 0 && cursor >= queue.length
  const current = !finished ? (queue[cursor] ?? null) : null
  const currentGif = current ? gifById.get(current.gifId) : undefined
  const currentReaction = current ? reactions[current.gifId] : undefined

  const startExplore = useCallback(async () => {
    if (!hasContext) {
      toast.error('Add a conversation snippet or user intent first')
      return
    }
    setExploring(true)
    setPoolExhausted(false)
    setQueue([])
    setStances([])
    setCursor(0)
    setReactions({})
    setStanceReactions({})
    try {
      const snippet = conversationSnippet.trim() || undefined
      const intent = userIntent.trim() || undefined

      // Run the recommender board and a retrieval-pool sample in parallel; if
      // one fails we still want the other's candidates.
      const [recResult, poolResult] = await Promise.allSettled([
        getRecommendations(snippet, intent, undefined, ExplicitRating.PG_13, {
          preset,
        }),
        getRetrievalPool(snippet, intent, POOL_K),
      ])

      const merged: QueuedCandidate[] = []
      const seen = new Set<string>()

      if (recResult.status === 'fulfilled') {
        setStances(
          (recResult.value.stances ?? []).map((s) => ({
            label: s.label,
            rationale: s.rationale,
          }))
        )
        // stanceLabel comes off each flat recommendation.
        for (const rec of recResult.value.recommendations) {
          if (seen.has(rec.gifId)) continue
          seen.add(rec.gifId)
          merged.push({
            gifId: rec.gifId,
            stanceLabel: rec.stanceLabel ?? null,
            reasoning: rec.reasoning || null,
          })
        }
      } else {
        toast.warning(
          `Recommender preview failed: ${recResult.reason instanceof Error ? recResult.reason.message : String(recResult.reason)}`
        )
      }

      if (poolResult.status === 'fulfilled') {
        for (const c of poolResult.value.candidates) {
          if (seen.has(c.gifId)) continue
          seen.add(c.gifId)
          merged.push({ gifId: c.gifId, stanceLabel: null, reasoning: null })
        }
      } else {
        toast.warning(
          `Retrieval pool failed: ${poolResult.reason instanceof Error ? poolResult.reason.message : String(poolResult.reason)}`
        )
      }

      if (merged.length === 0) {
        toast.error('No candidates surfaced — check the conversation and retry')
        return
      }
      setQueue(merged)
      toast.success(`Surfaced ${merged.length} candidates to react to`)
    } catch (err) {
      toast.error(
        `Explore failed: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setExploring(false)
    }
  }, [hasContext, conversationSnippet, userIntent, preset, toast])

  // Fetch the next tier of the retrieval pool and append the unseen tail —
  // reactions, chips, and comments on already-reacted cards stay intact. The
  // pool is a deterministic cosine ranking, so asking for a deeper k and
  // skipping ids already in the queue yields the next-best candidates.
  const loadMore = useCallback(async () => {
    const seen = new Set(queue.map((c) => c.gifId))
    setLoadingMore(true)
    try {
      const snippet = conversationSnippet.trim() || undefined
      const intent = userIntent.trim() || undefined
      const k = Math.min(POOL_MAX_K, seen.size + POOL_K)
      const pool = await getRetrievalPool(snippet, intent, k)
      const fresh = pool.candidates
        .filter((c) => !seen.has(c.gifId))
        .map((c) => ({ gifId: c.gifId, stanceLabel: null, reasoning: null }))
      if (fresh.length === 0) {
        setPoolExhausted(true)
        toast.warning('Retrieval pool exhausted — no unseen candidates left')
        return
      }
      if (k >= POOL_MAX_K) setPoolExhausted(true)
      setQueue((prev) => [...prev, ...fresh])
      toast.success(`Added ${fresh.length} more candidates`)
    } catch (err) {
      toast.error(
        `Load more failed: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setLoadingMore(false)
    }
  }, [queue, conversationSnippet, userIntent, toast])

  // Apply a verdict to the current card, mirror into the parent buckets, advance.
  const applyVerdict = useCallback(
    (verdict: Verdict) => {
      const pick = queue[cursor]
      if (!pick) return
      setReactions((prev) => {
        const existing = prev[pick.gifId]
        return {
          ...prev,
          [pick.gifId]: {
            verdict,
            reasons: existing?.reasons ?? [],
            comment: existing?.comment ?? '',
          },
        }
      })
      if (verdict === 'trap') onAssignBucket(pick.gifId, 'traps')
      else if (verdict === 'good') onAssignBucket(pick.gifId, 'good')
      else onRemoveBucket(pick.gifId) // skip clears any prior bucket write
      setCursor((c) => c + 1)
    },
    [queue, cursor, onAssignBucket, onRemoveBucket]
  )

  // Undo: step back, clear that card's verdict and its bucket write.
  const undo = useCallback(() => {
    setCursor((c) => {
      const target = Math.max(0, c - 1)
      const pick = queue[target]
      if (pick) {
        onRemoveBucket(pick.gifId)
        setReactions((prev) => {
          const next = { ...prev }
          delete next[pick.gifId]
          return next
        })
      }
      return target
    })
  }, [queue, onRemoveBucket])

  const toggleReason = useCallback((gifId: string, reason: string) => {
    setReactions((prev) => {
      const existing = prev[gifId]
      // Chips are optional; a bare verdict is fine. Toggling a chip before a
      // verdict exists defaults the card to skip so the chip has a home.
      const base: ReactionState = existing ?? {
        verdict: 'skip',
        reasons: [],
        comment: '',
      }
      const reasons = base.reasons.includes(reason)
        ? base.reasons.filter((r) => r !== reason)
        : [...base.reasons, reason]
      return { ...prev, [gifId]: { ...base, reasons } }
    })
  }, [])

  const setComment = useCallback((gifId: string, comment: string) => {
    setReactions((prev) => {
      const base: ReactionState = prev[gifId] ?? {
        verdict: 'skip',
        reasons: [],
        comment: '',
      }
      return { ...prev, [gifId]: { ...base, comment } }
    })
  }, [])

  // Keyboard triage on the current card: y/t/n verdict, u undo. Mirrors the
  // triage inbox exactly; ignored while typing in a field.
  useEffect(() => {
    if (queue.length === 0 || finished) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return
      }
      if (e.key === 'y') applyVerdict('good')
      else if (e.key === 't') applyVerdict('trap')
      else if (e.key === 'n') applyVerdict('skip')
      else if (e.key === 'u') undo()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [queue.length, finished, applyVerdict, undo])

  // Hand the collected reactions up to the parent, which runs the single
  // draftFixtureMeta call (merging its own trap/forbidden/good buckets) and
  // applies the per-field draft results.
  const draftFromReactions = useCallback(() => {
    if (nonSkipCount === 0) {
      toast.error('React to at least one candidate first (y or t)')
      return
    }
    const reactionPayload: FixtureNoteReaction[] = Object.entries(reactions)
      .filter(([, r]) => r.verdict !== 'skip')
      .map(([gifId, r]) => ({
        gifId,
        verdict: r.verdict,
        ...(r.reasons.length > 0 ? { reasons: r.reasons } : {}),
        ...(r.comment.trim() ? { comment: r.comment.trim() } : {}),
      }))

    const stancePayload: FixtureNoteStanceReaction[] = Object.entries(
      stanceReactions
    ).map(([label, verdict]) => ({ label, verdict }))

    onDraft(reactionPayload, stancePayload)
  }, [nonSkipCount, reactions, stanceReactions, onDraft, toast])

  const isRevision = hasNote

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Explore &amp; react</h2>
          <p className="text-xs text-default-500">
            React to concrete candidates instead of writing the note from a
            blank page. y → I&apos;d send it, t → wrong for this conversation
            (the system surfacing it is what makes it a trap — no need to feel
            tempted yourself), n → fine-but-unremarkable or can&apos;t judge, u
            → undo. Then draft the fixture from your reactions and edit each
            field to approve. (You can also skip this and just bucket gifs on
            the recommender board, then hit Draft fixture below.)
          </p>
        </div>
        <Button
          color="primary"
          className="shrink-0 whitespace-nowrap"
          onPress={() => void startExplore()}
          isLoading={exploring}
          isDisabled={!hasContext || exploring}
        >
          {queue.length > 0 ? 'Re-explore' : 'Explore & react'}
        </Button>
      </CardHeader>
      <CardBody className="space-y-4">
        {!hasContext && (
          <p className="text-sm text-default-500">
            Fill in the conversation snippet or user intent above to start.
          </p>
        )}

        {exploring && (
          <div className="flex justify-center py-8">
            <Spinner label="Surfacing candidates…" />
          </div>
        )}

        {/* Stance-level reaction row: verdict on whole proposed moves. */}
        {queue.length > 0 && stances.length > 0 && (
          <div className="rounded-lg border border-default-200 dark:border-default-100/20 p-3 space-y-2">
            <p className="text-xs font-medium text-default-600">
              Response moves the recommender proposed — mark each real or off:
            </p>
            <div className="flex flex-col gap-2">
              {stances.map((s) => {
                const v = stanceReactions[s.label]
                return (
                  <div
                    key={s.label}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.label}</p>
                      {s.rationale && (
                        <p className="text-xs text-default-500 truncate">
                          {s.rationale}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant={v === 'real-move' ? 'solid' : 'flat'}
                        color="success"
                        onPress={() =>
                          setStanceReactions((prev) =>
                            prev[s.label] === 'real-move'
                              ? (({ [s.label]: _omit, ...rest }) => rest)(prev)
                              : { ...prev, [s.label]: 'real-move' }
                          )
                        }
                      >
                        Real move
                      </Button>
                      <Button
                        size="sm"
                        variant={v === 'off' ? 'solid' : 'flat'}
                        color="warning"
                        onPress={() =>
                          setStanceReactions((prev) =>
                            prev[s.label] === 'off'
                              ? (({ [s.label]: _omit, ...rest }) => rest)(prev)
                              : { ...prev, [s.label]: 'off' }
                          )
                        }
                      >
                        Off
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* The current reaction card. */}
        {current && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-default-400">
                {cursor + 1} / {queue.length}
              </p>
              <div className="flex items-center gap-2">
                <Chip size="sm" variant="flat" color="success">
                  {
                    Object.values(reactions).filter((r) => r.verdict === 'good')
                      .length
                  }{' '}
                  good
                </Chip>
                <Chip size="sm" variant="flat" color="warning">
                  {
                    Object.values(reactions).filter((r) => r.verdict === 'trap')
                      .length
                  }{' '}
                  trap
                </Chip>
              </div>
            </div>

            <div className="flex flex-col items-center gap-3">
              {currentGif ? (
                <GifThumbnail
                  gif={currentGif}
                  className="w-56 h-56 rounded-lg"
                />
              ) : (
                <div className="w-56 h-56 rounded-lg bg-default-200 flex items-center justify-center text-xs text-default-500 text-center p-4">
                  GIF {current.gifId} is missing from the library — skip it
                </div>
              )}
              {currentGif && (
                <p className="text-sm text-default-600 text-center max-w-md">
                  {gifDisplayTitle(currentGif)}
                </p>
              )}
              {current.stanceLabel ? (
                <Chip size="sm" variant="flat" color="primary">
                  stance: {current.stanceLabel}
                </Chip>
              ) : (
                // Pool candidates were surfaced by embedding similarity alone —
                // no reranker saw them, so there's no stance or reasoning.
                <Chip size="sm" variant="flat" color="default">
                  retrieval pool — similarity only, no reranker reasoning
                </Chip>
              )}
              {current.reasoning && (
                <p className="text-xs text-default-500 text-center max-w-md italic">
                  &ldquo;{current.reasoning}&rdquo;
                </p>
              )}
              <p className="font-mono text-[10px] text-default-400">
                {current.gifId}
              </p>

              {/* Peek at the retrieval record (collapsed) — react to the GIF
                  first, then check the record if a pick looks subtly off. Keyed
                  by gifId so it collapses as the queue advances. */}
              <RecordPeek key={current.gifId} gifId={current.gifId} />

              <div className="flex gap-2 flex-wrap justify-center">
                <Button
                  color="success"
                  variant="flat"
                  onPress={() => applyVerdict('good')}
                >
                  Viable move (y)
                </Button>
                <Button
                  color="warning"
                  variant="flat"
                  onPress={() => applyVerdict('trap')}
                >
                  Wrong for this (t)
                </Button>
                <Button
                  color="default"
                  variant="flat"
                  onPress={() => applyVerdict('skip')}
                >
                  Skip (n)
                </Button>
                <Button
                  variant="light"
                  onPress={undo}
                  isDisabled={cursor === 0}
                >
                  Undo (u)
                </Button>
              </div>

              {/* Optional reason chips + free-text comment for the current gif. */}
              <div className="w-full max-w-md space-y-2">
                <div className="flex flex-wrap gap-1 justify-center">
                  {REASON_CHIPS.map((chip) => {
                    const active =
                      currentReaction?.reasons.includes(chip) ?? false
                    return (
                      <Chip
                        key={chip}
                        size="sm"
                        variant={active ? 'solid' : 'flat'}
                        color={active ? 'secondary' : 'default'}
                        className="cursor-pointer"
                        onClick={() => toggleReason(current.gifId, chip)}
                      >
                        {chip}
                      </Chip>
                    )
                  })}
                </div>
                <Textarea
                  size="sm"
                  minRows={1}
                  placeholder="Optional note (why it fits or misses)…"
                  value={currentReaction?.comment ?? ''}
                  onChange={(e) => setComment(current.gifId, e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {finished && (
          <div className="rounded-lg bg-default-50 dark:bg-default-100/10 p-4 text-center space-y-2">
            <p className="text-sm">
              Reacted to all {queue.length} candidates — {nonSkipCount} carr
              {nonSkipCount === 1 ? 'ies' : 'y'} signal.
            </p>
            <div className="flex justify-center gap-2">
              <Button
                color="primary"
                variant="flat"
                size="sm"
                onPress={() => void loadMore()}
                isLoading={loadingMore}
                isDisabled={loadingMore || poolExhausted}
              >
                {poolExhausted ? 'Pool exhausted' : 'More candidates'}
              </Button>
              <Button variant="flat" size="sm" onPress={() => setCursor(0)}>
                Review from the top
              </Button>
            </div>
          </div>
        )}

        {/* Draft / revise the fixture from the collected reactions + buckets. */}
        {queue.length > 0 && (
          <div className="pt-3 border-t border-default-200 dark:border-default-100/20 flex items-center justify-between gap-3">
            <p className="text-xs text-default-500">
              {isRevision
                ? 'Revises the note (and fills any empty fields) with your reactions + buckets.'
                : 'Drafts the note, slug, description, and tags from your reactions + buckets.'}
            </p>
            <Button
              color="primary"
              className="shrink-0 whitespace-nowrap"
              onPress={draftFromReactions}
              isLoading={drafting}
              isDisabled={drafting || nonSkipCount === 0}
            >
              {isRevision
                ? 'Revise fixture from reactions'
                : 'Draft fixture from reactions'}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
